import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Code2,
  Globe,
  Smartphone,
  Shield,
  Zap,
  Users,
  Award,
  Building2,
  Rocket,
  Brain,
  Heart,
  Package,
  ShoppingCart,
  Lock,
  Megaphone,
  Target,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Star,
  ArrowUpRight,
  Layers,
  TrendingUp,
  Clock,
  FlaskConical,
  ExternalLink,
  Quote,
  MapPin,
  Terminal,
  Database,
  Wifi,
  MessageSquare,
} from 'lucide-react';
import { FlagIcon } from '@/components/ui/FlagIcon';
import { LiveCodeEditor } from '@/components/ui/LiveCodeEditor';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import { useTranslation } from 'react-i18next';

// Products data
const featuredProducts = [
  {
    name: 'Ochtopus IDE',
    description: 'Yapay Zeka Destekli Kod Editörü - VS Code alternatifi olarak geliştirilen akıllı IDE',
    icon: Code2,
    color: 'from-purple-500 to-fuchsia-500',
    link: 'https://ochtopus.com',
    status: 'Geliştirme Aşamasında',
    featured: true,
  },
  {
    name: 'Minder',
    description: 'AI Destekli Sosyal Tanışma Uygulaması',
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    link: '/products#minder',
  },
  {
    name: 'Fulfillment',
    description: 'Akıllı Depo & Stok Yönetim Sistemi',
    icon: Package,
    color: 'from-blue-500 to-cyan-500',
    link: '/products#fulfillment',
  },
  {
    name: 'E-Ticaret Entegratör',
    description: 'Çoklu Pazar Yeri Entegrasyonu',
    icon: ShoppingCart,
    color: 'from-orange-500 to-amber-500',
    link: '/products#entegrator',
  },
  {
    name: 'CyberShield',
    description: 'Siber Güvenlik & Sızma Testi Platformu',
    icon: Lock,
    color: 'from-red-500 to-orange-500',
    link: '/products#cybershield',
  },
  {
    name: 'AdMaster',
    description: 'AI Destekli Dijital Reklam Yönetimi',
    icon: Megaphone,
    color: 'from-green-500 to-emerald-500',
    link: '/products#admaster',
  },
  {
    name: 'SalesTracker',
    description: 'Satış Takip ve Analiz Platformu',
    icon: TrendingUp,
    color: 'from-indigo-500 to-violet-500',
    link: '/products#salestracker',
  },
];

// ============================================================================
// SERVICE ANIMATION COMPONENTS
// ============================================================================

// Custom Software Animation
function CustomSoftwareAnimation() {
  const [activeLines, setActiveLines] = useState([0, 1, 2]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLines(prev => prev.map(line => (line + 1) % 8));
    }, 800);
    return () => clearInterval(interval);
  }, []);

  const codeLines = [
    { indent: 0, text: 'class UserService {', color: 'text-purple-400' },
    { indent: 1, text: 'async createUser(data) {', color: 'text-blue-400' },
    { indent: 2, text: 'const user = await db.create(data);', color: 'text-green-400' },
    { indent: 2, text: 'await notify.send(user);', color: 'text-yellow-400' },
    { indent: 2, text: 'return user;', color: 'text-cyan-400' },
    { indent: 1, text: '}', color: 'text-blue-400' },
    { indent: 0, text: '}', color: 'text-purple-400' },
    { indent: 0, text: '', color: '' },
  ];

  return (
    <div className="bg-[#1a1a2e] rounded-xl p-4 font-mono text-xs h-52 overflow-hidden">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
        </div>
        <span className="text-text-tertiary text-[10px]">service.ts</span>
      </div>
      {codeLines.map((line, i) => (
        <div
          key={i}
          className={`transition-all duration-300 py-0.5 rounded ${activeLines.includes(i) ? 'opacity-100 bg-purple-500/10' : 'opacity-60'}`}
          style={{ paddingLeft: `${line.indent * 16}px` }}
        >
          <span className={line.color}>{line.text}</span>
          {activeLines.includes(i) && <span className="animate-pulse text-purple-400">|</span>}
        </div>
      ))}
    </div>
  );
}

// Web Development Animation
function WebDevAnimation() {
  const [loading, setLoading] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setLoading(prev => (prev + 5) % 105);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-xl overflow-hidden h-52">
      <div className="flex items-center gap-2 p-2 bg-[#252540] border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
        </div>
        <div className="flex-1 bg-[#1a1a2e] rounded px-3 py-1 text-[10px] text-text-tertiary flex items-center gap-2">
          <Lock size={10} className="text-green-400" />
          www.example.com
        </div>
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="w-16 h-4 rounded bg-gradient-to-r from-blue-500/30 to-cyan-500/30 animate-pulse" />
          <div className="flex gap-2">
            <div className="w-10 h-3 rounded bg-white/10" />
            <div className="w-10 h-3 rounded bg-white/10" />
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="h-6 rounded bg-gradient-to-r from-blue-500/40 to-transparent transition-all duration-300" style={{ width: `${Math.min(loading, 100)}%` }} />
          <div className="h-3 rounded bg-white/20 transition-all duration-300" style={{ width: `${Math.min(loading * 0.8, 80)}%` }} />
        </div>
        <div className="flex gap-2 mt-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex-1 h-14 rounded bg-gradient-to-br from-blue-500/20 to-cyan-500/10 transition-all duration-500" style={{ opacity: loading > (i + 1) * 30 ? 1 : 0.3, transform: `translateY(${loading > (i + 1) * 30 ? 0 : 10}px)` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Mobile Development Animation
function MobileDevAnimation() {
  const [currentScreen, setCurrentScreen] = useState(0);
  const [floatY, setFloatY] = useState(0);

  useEffect(() => {
    const screenInterval = setInterval(() => setCurrentScreen(prev => (prev + 1) % 3), 2500);
    const floatInterval = setInterval(() => setFloatY(prev => prev === 0 ? 6 : 0), 1500);
    return () => { clearInterval(screenInterval); clearInterval(floatInterval); };
  }, []);

  const screens = [
    { name: 'Dashboard', color: 'from-purple-500 to-fuchsia-500' },
    { name: 'Analytics', color: 'from-blue-500 to-cyan-500' },
    { name: 'Settings', color: 'from-green-500 to-emerald-500' },
  ];

  return (
    <div className="flex items-center justify-center h-52 relative">
      <div className="relative transition-transform duration-700 ease-in-out" style={{ transform: `translateY(${floatY}px)` }}>
        <div className="w-28 h-48 rounded-[24px] bg-gradient-to-b from-gray-800 to-gray-900 p-1.5 shadow-2xl shadow-purple-500/20">
          <div className="w-full h-full rounded-[20px] bg-[#0a0a12] overflow-hidden relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-14 h-5 bg-[#0a0a12] rounded-b-xl z-10" />
            <div className={`absolute inset-0 bg-gradient-to-br ${screens[currentScreen].color} opacity-20`} />
            <div className="relative z-5 p-3 pt-7">
              <div className="text-center mb-3">
                <div className={`w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${screens[currentScreen].color} flex items-center justify-center mb-2`}>
                  <Smartphone size={18} className="text-white" />
                </div>
                <div className="text-[10px] text-white font-medium">{screens[currentScreen].name}</div>
              </div>
              <div className="space-y-1.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-2.5 rounded bg-white/10 transition-all duration-500" style={{ width: `${85 - i * 15}%`, opacity: currentScreen === i ? 1 : 0.5 }} />
                ))}
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex justify-around">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentScreen === i ? 'bg-white scale-125' : 'bg-white/30'}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute -right-4 top-6 w-3 h-3 rounded-full bg-purple-500/60 animate-pulse" />
        <div className="absolute -left-3 bottom-10 w-2 h-2 rounded-full bg-cyan-500/60 animate-pulse" style={{ animationDelay: '500ms' }} />
      </div>
    </div>
  );
}

// Cybersecurity Animation
function CybersecurityAnimation() {
  const [scanning, setScanning] = useState(0);
  const [threats, setThreats] = useState([false, false, false, false]);

  useEffect(() => {
    const interval = setInterval(() => {
      setScanning(prev => (prev + 1) % 100);
      if (Math.random() > 0.8) {
        setThreats(prev => {
          const next = [...prev];
          const idx = Math.floor(Math.random() * 4);
          next[idx] = true;
          setTimeout(() => setThreats(p => { const n = [...p]; n[idx] = false; return n; }), 800);
          return next;
        });
      }
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-xl p-4 h-52 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        {[...Array(8)].map((_, i) => (<div key={i} className="absolute w-full h-px bg-red-500" style={{ top: `${i * 12.5}%` }} />))}
        {[...Array(8)].map((_, i) => (<div key={i} className="absolute h-full w-px bg-red-500" style={{ left: `${i * 12.5}%` }} />))}
      </div>
      <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50" style={{ top: `${scanning}%` }} />
      <div className="flex flex-col items-center justify-center h-full relative z-10">
        <div className="relative">
          <Shield size={56} className={`${threats.some(t => t) ? 'text-red-500 animate-pulse' : 'text-green-500'} transition-colors`} />
          {threats.some(t => t) && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white animate-bounce">!</div>
          )}
        </div>
        <div className="mt-3 text-xs text-text-secondary">{threats.some(t => t) ? 'Threat Detected!' : 'System Secure'}</div>
        <div className="mt-2 flex gap-2">
          {threats.map((t, i) => (<div key={i} className={`w-2.5 h-2.5 rounded-full transition-colors ${t ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />))}
        </div>
      </div>
    </div>
  );
}

// AI Consulting Animation
function AIConsultingAnimation() {
  const [typing, setTyping] = useState('');
  const [response, setResponse] = useState('');
  const fullQuestion = 'What should we do to increase sales?';
  const fullResponse = 'AI analysis: Personalized recommendations based on customer behavior...';

  useEffect(() => {
    let qIdx = 0, rIdx = 0, phase = 'question';
    const interval = setInterval(() => {
      if (phase === 'question') {
        if (qIdx <= fullQuestion.length) { setTyping(fullQuestion.slice(0, qIdx)); qIdx++; }
        else { phase = 'wait'; setTimeout(() => { phase = 'response'; }, 400); }
      } else if (phase === 'response') {
        if (rIdx <= fullResponse.length) { setResponse(fullResponse.slice(0, rIdx)); rIdx++; }
        else { setTimeout(() => { setTyping(''); setResponse(''); qIdx = 0; rIdx = 0; phase = 'question'; }, 1500); }
      }
    }, 45);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#1a1a2e] rounded-xl p-4 h-52 flex flex-col">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
          <Brain size={14} className="text-white" />
        </div>
        <span className="text-xs text-text-secondary">AI Assistant</span>
        <div className="ml-auto flex gap-1 items-center">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[10px] text-green-400">Active</span>
        </div>
      </div>
      <div className="flex-1 space-y-2 overflow-hidden">
        {typing && (
          <div className="flex justify-end">
            <div className="bg-purple-500/20 rounded-xl rounded-br-none px-3 py-2 max-w-[85%]">
              <p className="text-xs text-text-primary">{typing}<span className="animate-pulse">|</span></p>
            </div>
          </div>
        )}
        {response && (
          <div className="flex justify-start">
            <div className="bg-violet-500/20 rounded-xl rounded-bl-none px-3 py-2 max-w-[85%]">
              <div className="flex items-center gap-1 mb-1">
                <Sparkles size={10} className="text-violet-400" />
                <span className="text-[10px] text-violet-400">AI</span>
              </div>
              <p className="text-xs text-text-primary">{response}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Services with detailed content for each
const services = [
  {
    id: 'custom-software',
    icon: Code2,
    title: 'Özel Yazılım',
    description: 'İhtiyaca özel web ve kurumsal çözümler',
    color: 'from-purple-500 to-violet-500',
    detailTitle: 'Özel Yazılım Geliştirme',
    detailDescription: 'İşletmenizin ihtiyaçlarına özel, ölçeklenebilir ve güvenli yazılım çözümleri geliştiriyoruz.',
    tags: ['ERP', 'CRM', 'API', 'Otomasyon'],
    Animation: CustomSoftwareAnimation,
  },
  {
    id: 'web-development',
    icon: Globe,
    title: 'Web Geliştirme',
    description: 'E-ticaret ve kurumsal siteler',
    color: 'from-blue-500 to-cyan-500',
    detailTitle: 'Modern Web Çözümleri',
    detailDescription: 'SEO uyumlu, hızlı ve mobil öncelikli web siteleri ve e-ticaret platformları oluşturuyoruz.',
    tags: ['E-Ticaret', 'Landing Page', 'Portal', 'PWA'],
    Animation: WebDevAnimation,
  },
  {
    id: 'mobile-development',
    icon: Smartphone,
    title: 'Mobil Uygulama',
    description: 'iOS, Android ve cross-platform',
    color: 'from-green-500 to-emerald-500',
    detailTitle: 'Mobil Uygulama Geliştirme',
    detailDescription: 'Native ve cross-platform teknolojilerle kullanıcı dostu mobil uygulamalar geliştiriyoruz.',
    tags: ['iOS', 'Android', 'React Native', 'Flutter'],
    Animation: MobileDevAnimation,
  },
  {
    id: 'cybersecurity',
    icon: Shield,
    title: 'Siber Güvenlik',
    description: 'Güvenlik testi ve danışmanlık',
    color: 'from-red-500 to-orange-500',
    detailTitle: 'Siber Güvenlik Hizmetleri',
    detailDescription: 'Penetrasyon testi, güvenlik denetimi ve KVKK/GDPR uyumluluk danışmanlığı sunuyoruz.',
    tags: ['Pentest', 'SIEM', 'SOC', 'Audit'],
    Animation: CybersecurityAnimation,
  },
  {
    id: 'ai-consulting',
    icon: Brain,
    title: 'AI Çözümleri',
    description: 'Yapay zeka entegrasyonu',
    color: 'from-pink-500 to-rose-500',
    detailTitle: 'Yapay Zeka Entegrasyonu',
    detailDescription: 'ChatBot, görüntü işleme, NLP ve özel AI modelleri ile işletmenizi akıllandırıyoruz.',
    tags: ['ChatBot', 'NLP', 'CV', 'ML'],
    Animation: AIConsultingAnimation,
  },
];

// Stats with animation
const stats = [
  { value: 8, suffix: '+', label: 'Yıllık Deneyim', icon: Clock },
  { value: 50, suffix: '+', label: 'Tamamlanan Proje', icon: Layers },
  { value: 9, suffix: '', label: 'Ülkede Hizmet', icon: Globe },
  { value: 100, suffix: '%', label: 'Müşteri Memnuniyeti', icon: Star },
];

// Countries served
const countries = [
  { code: 'tr', name: 'Türkiye' },
  { code: 'gb', name: 'İngiltere' },
  { code: 'fr', name: 'Fransa' },
  { code: 'us', name: 'ABD' },
  { code: 'ca', name: 'Kanada' },
  { code: 'az', name: 'Azerbaycan' },
  { code: 'cy', name: 'Kıbrıs' },
  { code: 'ma', name: 'Fas' },
  { code: 'mx', name: 'Meksika' },
];

// Why choose us
const advantages = [
  {
    icon: Zap,
    title: 'Hızlı Teslimat',
    description: 'Agile metodoloji ile zamanında proje teslimatı',
  },
  {
    icon: Shield,
    title: 'Güvenli Çözümler',
    description: 'OWASP standartlarına uygun güvenlik',
  },
  {
    icon: Users,
    title: 'Uzman Ekip',
    description: 'Akademisyen düzeyinde profesyoneller',
  },
  {
    icon: Award,
    title: 'Ödüllü Projeler',
    description: 'AI ve yazılım alanında başarılar',
  },
  {
    icon: TrendingUp,
    title: 'Sürekli Gelişim',
    description: 'En güncel teknolojiler ve yaklaşımlar',
  },
  {
    icon: Target,
    title: 'Sonuç Odaklı',
    description: 'İş hedeflerinize uygun çözümler',
  },
];

// Testimonials
const testimonials = [
  {
    quote: 'Bosphorus Labs ekibi, projemizi beklentilerimizin çok üzerinde teslim etti. Profesyonel yaklaşımları ve teknik uzmanlıkları gerçekten etkileyici.',
    author: 'Erkan E.',
    role: 'CEO',
    company: 'Dijital Ajans',
    rating: 5,
  },
  {
    quote: 'E-ticaret entegrasyon projemizde gösterdikleri performans mükemmeldi. Tüm pazaryerlerini tek panelden yönetebiliyoruz artık.',
    author: 'Selin K.',
    role: 'Operasyon Müdürü',
    company: 'E-Ticaret Şirketi',
    rating: 5,
  },
  {
    quote: 'AI danışmanlık hizmetleri sayesinde iş süreçlerimizi optimize ettik. Yatırımın geri dönüşü 6 ay içinde sağlandı.',
    author: 'Burak T.',
    role: 'CTO',
    company: 'Fintech Startup',
    rating: 5,
  },
];

// Animated counter hook
function useCounter(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [end, duration, hasStarted]);

  return { count, start: () => setHasStarted(true) };
}

// Stats Counter Component
function AnimatedStat({ stat, index }: { stat: typeof stats[0]; index: number }) {
  const { count, start } = useCounter(stat.value);

  useEffect(() => {
    const timer = setTimeout(() => start(), index * 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="text-center group">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent/20 to-purple-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
        <stat.icon className="text-accent" size={24} />
      </div>
      <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-accent via-purple-500 to-cyan-500 bg-clip-text text-transparent mb-2">
        {count}{stat.suffix}
      </div>
      <div className="text-text-secondary text-sm">{stat.label}</div>
    </div>
  );
}

export function HomePage() {
  const { t } = useTranslation();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [selectedService, setSelectedService] = useState(0);

  // Translated data
  const translatedFeaturedProducts = [
    {
      id: 'ochtopus',
      name: t('home.featuredProductsList.ochtopus.name'),
      description: t('home.featuredProductsList.ochtopus.description'),
      icon: Code2,
      color: 'from-purple-500 to-fuchsia-500',
      link: 'https://ochtopus.com',
      status: t('home.products.inDevelopment'),
      featured: true,
    },
    {
      id: 'minder',
      name: t('home.featuredProductsList.minder.name'),
      description: t('home.featuredProductsList.minder.description'),
      icon: Heart,
      color: 'from-pink-500 to-rose-500',
      link: '/products#minder',
    },
    {
      id: 'fulfillment',
      name: t('home.featuredProductsList.fulfillment.name'),
      description: t('home.featuredProductsList.fulfillment.description'),
      icon: Package,
      color: 'from-blue-500 to-cyan-500',
      link: '/products#fulfillment',
    },
    {
      id: 'entegrator',
      name: t('home.featuredProductsList.entegrator.name'),
      description: t('home.featuredProductsList.entegrator.description'),
      icon: ShoppingCart,
      color: 'from-orange-500 to-amber-500',
      link: '/products#entegrator',
    },
    {
      id: 'cybershield',
      name: t('home.featuredProductsList.cybershield.name'),
      description: t('home.featuredProductsList.cybershield.description'),
      icon: Lock,
      color: 'from-red-500 to-orange-500',
      link: '/products#cybershield',
    },
    {
      id: 'admaster',
      name: t('home.featuredProductsList.admaster.name'),
      description: t('home.featuredProductsList.admaster.description'),
      icon: Megaphone,
      color: 'from-green-500 to-emerald-500',
      link: '/products#admaster',
    },
    {
      id: 'salestracker',
      name: t('home.featuredProductsList.salestracker.name'),
      description: t('home.featuredProductsList.salestracker.description'),
      icon: TrendingUp,
      color: 'from-indigo-500 to-violet-500',
      link: '/products#salestracker',
    },
  ];

  const translatedStats = [
    { value: 8, suffix: '+', label: t('home.stats.yearsExperience'), icon: Clock },
    { value: 50, suffix: '+', label: t('home.stats.completedProjects'), icon: Layers },
    { value: 9, suffix: '', label: t('home.stats.countriesServed'), icon: Globe },
    { value: 100, suffix: '%', label: t('home.stats.customerSatisfaction'), icon: Star },
  ];

  const translatedCountries = [
    { code: 'tr', name: t('home.globalPresence.countryNames.tr') },
    { code: 'gb', name: t('home.globalPresence.countryNames.gb') },
    { code: 'fr', name: t('home.globalPresence.countryNames.fr') },
    { code: 'us', name: t('home.globalPresence.countryNames.us') },
    { code: 'ca', name: t('home.globalPresence.countryNames.ca') },
    { code: 'az', name: t('home.globalPresence.countryNames.az') },
    { code: 'cy', name: t('home.globalPresence.countryNames.cy') },
    { code: 'ma', name: t('home.globalPresence.countryNames.ma') },
    { code: 'mx', name: t('home.globalPresence.countryNames.mx') },
  ];

  const translatedAdvantages = [
    {
      icon: Zap,
      title: t('home.whyUs.items.fastDelivery.title'),
      description: t('home.whyUs.items.fastDelivery.description'),
    },
    {
      icon: Shield,
      title: t('home.whyUs.items.securesSolutions.title'),
      description: t('home.whyUs.items.securesSolutions.description'),
    },
    {
      icon: Users,
      title: t('home.whyUs.items.expertTeam.title'),
      description: t('home.whyUs.items.expertTeam.description'),
    },
    {
      icon: Award,
      title: t('home.whyUs.items.awardWinning.title'),
      description: t('home.whyUs.items.awardWinning.description'),
    },
    {
      icon: TrendingUp,
      title: t('home.whyUs.items.continuousGrowth.title'),
      description: t('home.whyUs.items.continuousGrowth.description'),
    },
    {
      icon: Target,
      title: t('home.whyUs.items.resultOriented.title'),
      description: t('home.whyUs.items.resultOriented.description'),
    },
  ];

  const translatedTestimonials = (t('home.testimonials.items', { returnObjects: true }) as Array<{
    quote: string;
    author: string;
    role: string;
    company: string;
  }>).map((item, index) => ({
    ...item,
    rating: 5,
  }));

  const translatedServices = [
    {
      id: 'custom-software',
      icon: Code2,
      title: t('home.serviceItems.custom-software.title'),
      description: t('home.serviceItems.custom-software.description'),
      color: 'from-purple-500 to-violet-500',
      detailTitle: t('home.serviceItems.custom-software.detailTitle'),
      detailDescription: t('home.serviceItems.custom-software.detailDescription'),
      tags: t('home.serviceItems.custom-software.tags', { returnObjects: true }) as string[],
      Animation: CustomSoftwareAnimation,
    },
    {
      id: 'web-development',
      icon: Globe,
      title: t('home.serviceItems.web-development.title'),
      description: t('home.serviceItems.web-development.description'),
      color: 'from-blue-500 to-cyan-500',
      detailTitle: t('home.serviceItems.web-development.detailTitle'),
      detailDescription: t('home.serviceItems.web-development.detailDescription'),
      tags: t('home.serviceItems.web-development.tags', { returnObjects: true }) as string[],
      Animation: WebDevAnimation,
    },
    {
      id: 'mobile-development',
      icon: Smartphone,
      title: t('home.serviceItems.mobile-development.title'),
      description: t('home.serviceItems.mobile-development.description'),
      color: 'from-green-500 to-emerald-500',
      detailTitle: t('home.serviceItems.mobile-development.detailTitle'),
      detailDescription: t('home.serviceItems.mobile-development.detailDescription'),
      tags: t('home.serviceItems.mobile-development.tags', { returnObjects: true }) as string[],
      Animation: MobileDevAnimation,
    },
    {
      id: 'cybersecurity',
      icon: Shield,
      title: t('home.serviceItems.cybersecurity.title'),
      description: t('home.serviceItems.cybersecurity.description'),
      color: 'from-red-500 to-orange-500',
      detailTitle: t('home.serviceItems.cybersecurity.detailTitle'),
      detailDescription: t('home.serviceItems.cybersecurity.detailDescription'),
      tags: t('home.serviceItems.cybersecurity.tags', { returnObjects: true }) as string[],
      Animation: CybersecurityAnimation,
    },
    {
      id: 'ai-consulting',
      icon: Brain,
      title: t('home.serviceItems.ai-solutions.title'),
      description: t('home.serviceItems.ai-solutions.description'),
      color: 'from-pink-500 to-rose-500',
      detailTitle: t('home.serviceItems.ai-solutions.detailTitle'),
      detailDescription: t('home.serviceItems.ai-solutions.detailDescription'),
      tags: t('home.serviceItems.ai-solutions.tags', { returnObjects: true }) as string[],
      Animation: AIConsultingAnimation,
    },
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <SEO {...SEO_CONFIG.home} />
      <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 pb-32">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Base */}
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />

          {/* Animated Orbs */}
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-purple-500/15 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '2s' }} />

          {/* Grid Pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(139, 92, 246, 0.5) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />

          {/* Floating Code Brackets */}
          <div className="absolute top-[15%] left-[10%] text-6xl font-mono animate-float opacity-[0.06]" style={{ animationDuration: '8s', color: '#8B5CF6' }}>{'<>'}</div>
          <div className="absolute top-[25%] right-[15%] text-5xl font-mono animate-float opacity-[0.06]" style={{ animationDuration: '10s', animationDelay: '1s', color: '#A855F7' }}>{'{ }'}</div>
          <div className="absolute bottom-[30%] left-[20%] text-4xl font-mono animate-float opacity-[0.06]" style={{ animationDuration: '9s', animationDelay: '2s', color: '#06B6D4' }}>{'</>'}</div>
          <div className="absolute bottom-[20%] right-[10%] text-5xl font-mono animate-float opacity-[0.06]" style={{ animationDuration: '7s', animationDelay: '0.5s', color: '#8B5CF6' }}>{'[ ]'}</div>
          <div className="absolute top-[40%] left-[5%] text-3xl font-mono animate-float opacity-[0.06]" style={{ animationDuration: '11s', animationDelay: '3s', color: '#A855F7' }}>{'()'}</div>
          <div className="absolute top-[60%] right-[25%] text-4xl font-mono animate-float opacity-[0.06]" style={{ animationDuration: '8s', animationDelay: '1.5s', color: '#06B6D4' }}>{'#'}</div>

          {/* Floating Tech Icons */}
          <div className="absolute top-[20%] left-[30%] animate-float opacity-[0.06]" style={{ animationDuration: '12s', color: '#8B5CF6' }}>
            <Code2 size={40} />
          </div>
          <div className="absolute top-[35%] right-[20%] animate-float opacity-[0.06]" style={{ animationDuration: '10s', animationDelay: '2s', color: '#A855F7' }}>
            <Brain size={50} />
          </div>
          <div className="absolute bottom-[35%] left-[15%] animate-float opacity-[0.06]" style={{ animationDuration: '9s', animationDelay: '1s', color: '#06B6D4' }}>
            <Rocket size={35} />
          </div>
          <div className="absolute bottom-[25%] right-[30%] animate-float opacity-[0.06]" style={{ animationDuration: '11s', animationDelay: '3s', color: '#22C55E' }}>
            <Shield size={45} />
          </div>
          <div className="absolute top-[50%] left-[40%] animate-float opacity-[0.06]" style={{ animationDuration: '8s', animationDelay: '0.5s', color: '#EC4899' }}>
            <Zap size={30} />
          </div>
          <div className="absolute top-[15%] right-[35%] animate-float opacity-[0.06]" style={{ animationDuration: '13s', animationDelay: '2.5s', color: '#F97316' }}>
            <Globe size={35} />
          </div>

          {/* Binary Rain Effect */}
          <div className="absolute top-0 left-[8%] text-accent/5 font-mono text-xs animate-binary-rain" style={{ animationDuration: '15s' }}>
            {'10110100'}
          </div>
          <div className="absolute top-0 left-[25%] text-purple-500/5 font-mono text-xs animate-binary-rain" style={{ animationDuration: '18s', animationDelay: '2s' }}>
            {'01001101'}
          </div>
          <div className="absolute top-0 left-[45%] text-cyan-500/5 font-mono text-xs animate-binary-rain" style={{ animationDuration: '20s', animationDelay: '4s' }}>
            {'11010010'}
          </div>
          <div className="absolute top-0 left-[65%] text-accent/5 font-mono text-xs animate-binary-rain" style={{ animationDuration: '16s', animationDelay: '1s' }}>
            {'00101101'}
          </div>
          <div className="absolute top-0 left-[85%] text-purple-500/5 font-mono text-xs animate-binary-rain" style={{ animationDuration: '22s', animationDelay: '3s' }}>
            {'10011010'}
          </div>

          {/* Connecting Lines / Neural Network Effect */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#06B6D4" />
              </linearGradient>
            </defs>
            {/* Animated connection lines */}
            <line x1="10%" y1="20%" x2="30%" y2="40%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" />
            <line x1="30%" y1="40%" x2="50%" y2="25%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
            <line x1="50%" y1="25%" x2="70%" y2="45%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '1s' }} />
            <line x1="70%" y1="45%" x2="90%" y2="30%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
            <line x1="20%" y1="60%" x2="40%" y2="75%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '2s' }} />
            <line x1="40%" y1="75%" x2="60%" y2="65%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '2.5s' }} />
            <line x1="60%" y1="65%" x2="80%" y2="80%" stroke="url(#line-gradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '3s' }} />
            {/* Connection nodes */}
            <circle cx="10%" cy="20%" r="3" fill="#8B5CF6" className="animate-pulse" fillOpacity="0.3" />
            <circle cx="30%" cy="40%" r="3" fill="#8B5CF6" className="animate-pulse" fillOpacity="0.3" style={{ animationDelay: '0.5s' }} />
            <circle cx="50%" cy="25%" r="3" fill="#A855F7" className="animate-pulse" fillOpacity="0.3" style={{ animationDelay: '1s' }} />
            <circle cx="70%" cy="45%" r="3" fill="#06B6D4" className="animate-pulse" fillOpacity="0.3" style={{ animationDelay: '1.5s' }} />
            <circle cx="90%" cy="30%" r="3" fill="#06B6D4" className="animate-pulse" fillOpacity="0.3" style={{ animationDelay: '2s' }} />
          </svg>

          {/* Floating Particles */}
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${2 + Math.random() * 4}px`,
                height: `${2 + Math.random() * 4}px`,
                backgroundColor: ['#8B5CF6', '#A855F7', '#06B6D4', '#22C55E'][Math.floor(Math.random() * 4)],
                opacity: 0.04 + Math.random() * 0.06,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${5 + Math.random() * 10}s`,
              }}
            />
          ))}

          {/* Decorative Lines */}
          <div className="absolute top-0 left-1/3 w-px h-full bg-gradient-to-b from-transparent via-accent/20 to-transparent" />
          <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-purple-500/20 to-transparent" />

          {/* Horizontal scan line effect */}
          <div className="absolute left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent animate-scan-line" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-accent/10 to-purple-500/10 border border-accent/30 mb-8">
              <div className="flex -space-x-1">
                {['tr', 'gb', 'fr', 'us', 'ca'].map((code) => (
                  <img
                    key={code}
                    src={`https://hatscripts.github.io/circle-flags/flags/${code}.svg`}
                    alt={code}
                    className="w-6 h-6 rounded-full shadow-sm"
                  />
                ))}
              </div>
              <span className="text-sm font-medium text-text-secondary">
                {t('home.hero.badge')}
              </span>
            </div>

            {/* Company Name - Primary heading for Google verification */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-violet-500 bg-clip-text text-transparent">
              {t('home.hero.companyName')}
            </h1>

            {/* Tagline */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
              {t('home.hero.title')}{' '}
              <span className="relative">
                <span className="bg-gradient-to-r from-accent via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                  {t('home.hero.titleHighlight')}
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 10C50 4 100 2 150 6C200 10 250 4 298 8" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="gradient" x1="0" y1="0" x2="300" y2="0">
                      <stop offset="0%" stopColor="#8B5CF6"/>
                      <stop offset="50%" stopColor="#A855F7"/>
                      <stop offset="100%" stopColor="#06B6D4"/>
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-text-secondary mb-12 max-w-3xl mx-auto leading-relaxed">
              {t('home.hero.description')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Link
                to="/contact"
                className="group relative px-8 py-4 bg-gradient-to-r from-accent to-purple-600 rounded-xl font-semibold text-white shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all duration-300 hover:-translate-y-1"
              >
                <span className="flex items-center gap-2">
                  <Rocket size={20} />
                  {t('home.hero.cta.primary')}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              <Link
                to="/products"
                className="group px-8 py-4 rounded-xl font-semibold border border-border-default hover:border-accent/50 bg-bg-surface/50 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1"
              >
                <span className="flex items-center gap-2">
                  {t('home.hero.cta.secondary')}
                  <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-text-secondary text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-green-400" size={18} />
                <span>{t('home.hero.trust.freeConsulting')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-green-400" size={18} />
                <span>{t('home.hero.trust.support247')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-green-400" size={18} />
                <span>{t('home.hero.trust.privacyGuarantee')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-accent/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-accent rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-sm relative -mt-16">
        <div className="container">
          <div className="relative rounded-3xl bg-bg-surface/80 backdrop-blur-xl border border-border-default p-8 md:p-12 shadow-2xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {translatedStats.map((stat, index) => (
                <AnimatedStat key={stat.label} stat={stat} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Product - Ochtopus */}
      <section className="section">
        <div className="container">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-purple-500/10 via-accent/5 to-cyan-500/10 border border-accent/20 p-8 md:p-12">
            {/* Background Effects */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-[80px]" />

            <div className="relative">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-accent/20 via-purple-500/10 to-cyan-500/10 border border-accent/30 mb-4">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-accent/20 border border-accent/30">
                      <FlaskConical size={12} className="text-accent" />
                      <span className="text-xs font-bold text-accent">{t('home.featuredProduct.betaBadge')}</span>
                    </div>
                    <div className="w-px h-4 bg-border-default" />
                    <Sparkles size={14} className="text-accent" />
                    <span className="text-sm font-medium text-text-secondary">{t('home.featuredProduct.badge')}</span>
                  </div>

                  <h2 className="text-4xl md:text-5xl font-bold">
                    <span className="bg-gradient-to-r from-accent via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                      Ochtopus IDE
                    </span>
                  </h2>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://ochtopus.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative px-6 py-3 bg-gradient-to-r from-accent to-purple-600 rounded-xl font-semibold text-white shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all duration-300 hover:-translate-y-1"
                  >
                    <span className="flex items-center gap-2">
                      <ExternalLink size={18} />
                      {t('home.featuredProduct.visitSite')}
                    </span>
                  </a>
                  <Link
                    to="/products#ochtopus"
                    className="px-6 py-3 rounded-xl font-semibold border border-border-default hover:border-accent/50 bg-bg-surface/50 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1"
                  >
                    <span className="flex items-center gap-2">
                      {t('home.featuredProduct.viewDetails')}
                      <ArrowUpRight size={18} />
                    </span>
                  </Link>
                </div>
              </div>

              {/* Description */}
              <p className="text-xl text-text-secondary mb-6 max-w-3xl leading-relaxed">
                {t('home.featuredProduct.description')}
              </p>

              {/* Feature Tags */}
              <div className="flex flex-wrap gap-3 mb-10">
                {[
                  { label: t('home.featuredProduct.features.multiAI'), icon: Brain },
                  { label: t('home.featuredProduct.features.smartAutocomplete'), icon: Sparkles },
                  { label: t('home.featuredProduct.features.realTimeCollab'), icon: Users },
                  { label: t('home.featuredProduct.features.builtInTerminal'), icon: Code2 },
                  { label: t('home.featuredProduct.features.gitIntegration'), icon: Layers },
                  { label: t('home.featuredProduct.features.extensionMarketplace'), icon: Package },
                ].map((feature) => (
                  <span
                    key={feature.label}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-bg-surface/80 backdrop-blur-sm border border-border-default text-sm hover:border-accent/50 transition-colors"
                  >
                    <feature.icon size={14} className="text-accent" />
                    {feature.label}
                  </span>
                ))}
              </div>

              {/* Live Code Editor */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e1e2e] via-transparent to-transparent z-10 pointer-events-none" />
                <LiveCodeEditor />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Yerli ve Milli AI Section */}
      <section className="section-sm relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-500/5 via-transparent to-red-500/5" />
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-64 h-64 bg-red-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-64 h-64 bg-accent/10 rounded-full blur-[100px]" />

        <div className="container relative">
          <div className="relative rounded-3xl border border-red-500/20 bg-gradient-to-br from-red-500/5 via-bg-surface/50 to-accent/5 backdrop-blur-sm p-8 md:p-12">
            {/* Turkish Flag Accent */}
            <div className="absolute -top-3 left-8 flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500 text-white text-xs font-semibold shadow-lg">
              <span>🇹🇷</span>
              <span>{t('home.localAI.badgeTop')}</span>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Content */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-medium mb-4">
                  <FlaskConical size={12} />
                  <span>{t('home.localAI.badge')}</span>
                </div>

                <h3 className="text-4xl md:text-5xl font-bold mb-4">
                  {t('home.localAI.title')}{' '}
                  <span className="bg-gradient-to-r from-red-500 to-accent bg-clip-text text-transparent">
                    {t('home.localAI.titleHighlight')}
                  </span>
                </h3>

                <p className="text-text-secondary mb-6 leading-relaxed">
                  {t('home.localAI.description')}
                </p>

                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-card border border-border-default">
                    <Brain size={16} className="text-accent" />
                    <span className="text-sm text-text-secondary">{t('home.localAI.features.localLLM')}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-card border border-border-default">
                    <Code2 size={16} className="text-red-400" />
                    <span className="text-sm text-text-secondary">{t('home.localAI.features.turkishCode')}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-card border border-border-default">
                    <Shield size={16} className="text-green-400" />
                    <span className="text-sm text-text-secondary">{t('home.localAI.features.dataSecurity')}</span>
                  </div>
                </div>
              </div>

              {/* Visual */}
              <div className="relative">
                <div className="relative rounded-xl bg-[#1a1a2e] border border-white/10 p-6 overflow-hidden">
                  {/* Animated Background */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-white to-red-500 animate-pulse" />
                  </div>

                  <div className="relative">
                    {/* AI Brain Icon */}
                    <div className="flex justify-center mb-4">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 to-accent flex items-center justify-center shadow-lg shadow-red-500/20">
                          <Brain size={40} className="text-white" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                          <Sparkles size={12} className="text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Model Name */}
                    <div className="text-center mb-4">
                      <h4 className="text-lg font-bold text-white mb-1">{t('home.localAI.modelName')}</h4>
                      <p className="text-xs text-text-tertiary">{t('home.localAI.modelSubtitle')}</p>
                    </div>

                    {/* Progress Indicators */}
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-text-tertiary">{t('home.localAI.progress.modelTraining')}</span>
                          <span className="text-accent">{t('home.localAI.progress.inProgress')}</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full w-3/5 bg-gradient-to-r from-red-500 to-accent rounded-full animate-pulse" />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-text-tertiary">{t('home.localAI.progress.datasetPreparation')}</span>
                          <span className="text-green-400">{t('home.localAI.progress.completed')}</span>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full w-full bg-green-500 rounded-full" />
                        </div>
                      </div>
                    </div>

                    {/* Coming Soon Badge */}
                    <div className="mt-4 text-center">
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent text-sm font-medium">
                        <Clock size={14} />
                        {t('home.localAI.comingSoon')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent text-sm mb-4">
              <Layers size={16} />
              <span>{t('home.products.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('home.products.title')}{' '}
              <span className="bg-gradient-to-r from-accent to-purple-500 bg-clip-text text-transparent">
                {t('home.products.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              {t('home.products.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {translatedFeaturedProducts.filter(p => !p.featured).map((product) => (
              <Link
                key={product.name}
                to={product.link}
                className="group relative rounded-2xl bg-bg-primary border border-border-default p-6 hover:border-accent/50 transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${product.color} p-3 mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <product.icon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-accent transition-colors">
                  {product.name}
                </h3>
                <p className="text-text-secondary text-sm">{product.description}</p>
                <ArrowUpRight className="absolute top-6 right-6 text-text-tertiary group-hover:text-accent transition-colors" size={20} />
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products" className="btn btn-secondary">
              {t('home.products.viewAll')}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-400 text-sm mb-4">
              <Code2 size={16} />
              <span>{t('home.servicesSection.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('home.servicesSection.title')}{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {t('home.servicesSection.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              {t('home.servicesSection.description')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Service List */}
            <div className="space-y-3">
              {translatedServices.map((service, index) => (
                <div
                  key={service.title}
                  onClick={() => setSelectedService(index)}
                  className={`group flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 cursor-pointer ${
                    selectedService === index
                      ? 'border-accent bg-accent/10 shadow-lg shadow-accent/10 scale-[1.02]'
                      : 'border-border-default hover:border-accent/50 bg-bg-surface/50'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} p-2.5 transition-transform ${
                    selectedService === index ? 'scale-110' : 'group-hover:scale-110'
                  }`}>
                    <service.icon className="w-full h-full text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold transition-colors ${
                      selectedService === index ? 'text-accent' : 'group-hover:text-accent'
                    }`}>{service.title}</h4>
                    <p className="text-text-secondary text-sm">{service.description}</p>
                  </div>
                  <ChevronRight className={`transition-all ${
                    selectedService === index
                      ? 'text-accent translate-x-1'
                      : 'text-text-tertiary group-hover:text-accent group-hover:translate-x-1'
                  }`} size={20} />
                </div>
              ))}
            </div>

            {/* Animated Service Details Panel */}
            <div className="lg:sticky lg:top-24">
              <div className="rounded-2xl border border-border-default bg-bg-surface overflow-hidden">
                {/* Animation Area */}
                <div key={selectedService} className="animate-fade-in">
                  {(() => {
                    const AnimationComponent = translatedServices[selectedService].Animation;
                    return <AnimationComponent />;
                  })()}
                </div>

                {/* Service Info */}
                <div className="p-6 border-t border-border-default">
                  <h3 className="text-xl font-bold mb-2">{translatedServices[selectedService].detailTitle}</h3>
                  <p className="text-text-secondary text-sm mb-4">
                    {translatedServices[selectedService].detailDescription}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {translatedServices[selectedService].tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r ${translatedServices[selectedService].color} bg-opacity-10`}
                        style={{
                          backgroundColor: selectedService === 0 ? 'rgba(139, 92, 246, 0.15)' :
                            selectedService === 1 ? 'rgba(6, 182, 212, 0.15)' :
                            selectedService === 2 ? 'rgba(16, 185, 129, 0.15)' :
                            selectedService === 3 ? 'rgba(249, 115, 22, 0.15)' :
                            'rgba(236, 72, 153, 0.15)',
                          color: selectedService === 0 ? '#A78BFA' :
                            selectedService === 1 ? '#22D3EE' :
                            selectedService === 2 ? '#34D399' :
                            selectedService === 3 ? '#FB923C' : '#F472B6',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Link
                    to={`/services#${translatedServices[selectedService].id}`}
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r ${translatedServices[selectedService].color} shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5`}
                  >
                    {t('home.servicesSection.viewDetails')}
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              {/* Service indicator dots */}
              <div className="flex justify-center gap-2 mt-4">
                {translatedServices.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedService(idx)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      selectedService === idx
                        ? 'w-8 bg-accent'
                        : 'w-2 bg-border-default hover:bg-text-tertiary'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* All Services Button */}
          <div className="flex justify-center mt-12">
            <Link
              to="/services"
              className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-2xl border-2 border-dashed border-accent/30 hover:border-accent bg-accent/5 hover:bg-accent/10 transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Layers size={20} className="text-accent" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-text-primary group-hover:text-accent transition-colors">
                  {t('home.servicesSection.allServices')}
                </div>
                <div className="text-sm text-text-secondary">
                  {t('home.servicesSection.allServicesDesc')}
                </div>
              </div>
              <ArrowRight size={20} className="text-accent group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section bg-bg-surface">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 text-green-400 text-sm mb-4">
              <Award size={16} />
              <span>{t('home.whyUs.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('home.whyUs.title')}{' '}
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                {t('home.whyUs.titleHighlight')}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {translatedAdvantages.map((advantage, index) => (
              <div
                key={advantage.title}
                className="group p-6 rounded-2xl bg-bg-primary border border-border-default hover:border-green-500/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-green-500/10 flex items-center justify-center mb-5 group-hover:bg-green-500/20 transition-colors">
                  <advantage.icon className="text-green-400" size={28} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{advantage.title}</h3>
                <p className="text-text-secondary text-sm">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 text-yellow-400 text-sm mb-4">
              <Quote size={16} />
              <span>{t('home.testimonials.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('home.testimonials.title')}{' '}
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                {t('home.testimonials.titleHighlight')}
              </span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-3xl bg-bg-surface border border-border-default p-8 md:p-12">
              {/* Quote icon - positioned in corner with low opacity */}
              <Quote className="absolute -top-3 -left-3 text-accent/10 rotate-180" size={32} />
              <Quote className="absolute -bottom-3 -right-3 text-accent/10" size={32} />

              <div className="relative">
                <p className="text-xl md:text-2xl text-text-primary mb-8 leading-relaxed italic">
                  "{translatedTestimonials[activeTestimonial]?.quote}"
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center text-white font-bold text-xl">
                      {translatedTestimonials[activeTestimonial]?.author?.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold">{translatedTestimonials[activeTestimonial]?.author}</div>
                      <div className="text-text-secondary text-sm">
                        {translatedTestimonials[activeTestimonial]?.role}, {translatedTestimonials[activeTestimonial]?.company}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-1">
                    {[...Array(translatedTestimonials[activeTestimonial]?.rating || 5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {translatedTestimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === activeTestimonial
                        ? 'w-8 bg-accent'
                        : 'bg-border-default hover:bg-accent/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="section bg-bg-surface relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/5 rounded-full blur-[150px]" />
          {/* Decorative world map dots pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `radial-gradient(circle, #3B82F6 1px, transparent 1px)`,
            backgroundSize: '30px 30px'
          }} />
        </div>

        <div className="container relative">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-400 text-sm mb-4">
              <Globe size={16} />
              <span>{t('home.globalPresence.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {t('home.globalPresence.title')}{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                {t('home.globalPresence.titleHighlight')}
              </span>
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              {t('home.globalPresence.description')}
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            {/* Left - Stats */}
            <div className="space-y-6">
              <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20">
                <div className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  9
                </div>
                <div className="text-text-secondary">{t('home.globalPresence.stats.countries')}</div>
              </div>
              <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/5 border border-purple-500/20">
                <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                  4
                </div>
                <div className="text-text-secondary">{t('home.globalPresence.stats.continents')}</div>
              </div>
              <div className="p-6 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20">
                <div className="text-5xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">
                  7/24
                </div>
                <div className="text-text-secondary">{t('home.globalPresence.stats.support')}</div>
              </div>
            </div>

            {/* Center - Turkey Hub with Connections */}
            <div className="relative flex items-center justify-center py-16">
              {/* Connection Lines from Turkey to each country */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 300">
                <defs>
                  <linearGradient id="line-grad-out" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
                {/* Lines to each flag position (8 flags in circle) */}
                <line x1="150" y1="150" x2="150" y2="30" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" />
                <line x1="150" y1="150" x2="235" y2="65" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.2s' }} />
                <line x1="150" y1="150" x2="270" y2="150" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.4s' }} />
                <line x1="150" y1="150" x2="235" y2="235" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.6s' }} />
                <line x1="150" y1="150" x2="150" y2="270" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '0.8s' }} />
                <line x1="150" y1="150" x2="65" y2="235" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '1s' }} />
                <line x1="150" y1="150" x2="30" y2="150" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
                <line x1="150" y1="150" x2="65" y2="65" stroke="url(#line-grad-out)" strokeWidth="1.5" className="animate-pulse" style={{ animationDelay: '1.4s' }} />
              </svg>

              {/* Country Flags in Perfect Circle */}
              <div className="absolute inset-0 flex items-center justify-center">
                {/* France - Top (0°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/fr.svg"
                  alt="France"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ top: '5%', left: 'calc(50% - 18px)' }}
                />
                {/* USA - Top Right (45°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/us.svg"
                  alt="USA"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ top: '15%', right: '15%' }}
                />
                {/* Canada - Right (90°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/ca.svg"
                  alt="Canada"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ top: 'calc(50% - 18px)', right: '5%' }}
                />
                {/* Mexico - Bottom Right (135°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/mx.svg"
                  alt="Mexico"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ bottom: '15%', right: '15%' }}
                />
                {/* Morocco - Bottom (180°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/ma.svg"
                  alt="Morocco"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ bottom: '5%', left: 'calc(50% - 18px)' }}
                />
                {/* Azerbaijan - Bottom Left (225°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/az.svg"
                  alt="Azerbaijan"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ bottom: '15%', left: '15%' }}
                />
                {/* Cyprus - Left (270°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/cy.svg"
                  alt="Cyprus"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ top: 'calc(50% - 18px)', left: '5%' }}
                />
                {/* UK - Top Left (315°) */}
                <img
                  src="https://hatscripts.github.io/circle-flags/flags/gb.svg"
                  alt="UK"
                  className="absolute w-9 h-9 rounded-full shadow-lg border-2 border-white/30 hover:scale-110 transition-transform"
                  style={{ top: '15%', left: '15%' }}
                />
              </div>

              {/* Turkey Hub - Center (smaller) */}
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-full shadow-2xl shadow-red-500/40 border-4 border-white/30 overflow-hidden">
                  <img
                    src="https://hatscripts.github.io/circle-flags/flags/tr.svg"
                    alt="Türkiye"
                    className="w-full h-full"
                  />
                </div>
                {/* Pulse effect */}
                <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" style={{ animationDuration: '2s' }} />
                <div className="absolute -inset-3 rounded-full border border-red-500/30 animate-pulse" />
                <div className="absolute -inset-6 rounded-full border border-red-500/20 animate-pulse" style={{ animationDelay: '0.5s' }} />
              </div>
            </div>

            {/* Right - Countries Grid */}
            <div className="grid grid-cols-2 gap-3">
              {translatedCountries.filter(c => c.code !== 'tr').map((country, index) => (
                <div
                  key={country.code}
                  className="group relative p-4 rounded-xl bg-bg-card border border-border-default hover:border-blue-500/50 hover:bg-blue-500/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-500/10"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <FlagIcon code={country.code as any} size={32} />
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-bg-card" />
                    </div>
                    <div>
                      <div className="font-medium text-text-primary group-hover:text-blue-400 transition-colors">
                        {country.name}
                      </div>
                      <div className="text-xs text-text-tertiary">{t('home.globalPresence.active')}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom - Regions */}
          <div className="mt-16 flex flex-wrap justify-center gap-6">
            {[
              { region: t('home.globalPresence.regions.europe.name'), countries: t('home.globalPresence.regions.europe.countries', { returnObjects: true }) as string[], color: 'from-blue-500 to-indigo-500', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20' },
              { region: t('home.globalPresence.regions.americas.name'), countries: t('home.globalPresence.regions.americas.countries', { returnObjects: true }) as string[], color: 'from-emerald-500 to-teal-500', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20' },
              { region: t('home.globalPresence.regions.asiaAfrica.name'), countries: t('home.globalPresence.regions.asiaAfrica.countries', { returnObjects: true }) as string[], color: 'from-orange-500 to-amber-500', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/20' },
            ].map((item) => (
              <div key={item.region} className={`flex items-center gap-4 px-6 py-4 rounded-2xl ${item.bgColor} border ${item.borderColor} hover:scale-105 transition-transform`}>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg`}>
                  <Globe size={24} className="text-white" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-text-primary">{item.region}</div>
                  <div className="text-xs text-text-secondary">{item.countries.join(' • ')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-lg relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-purple-500/5 to-cyan-500/10" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px]" />

        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center mx-auto mb-8 shadow-lg shadow-accent/30">
              <Rocket className="text-white" size={40} />
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {t('home.cta.title')}{' '}
              <span className="bg-gradient-to-r from-accent via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                {t('home.cta.titleHighlight')}
              </span>
            </h2>

            <p className="text-xl text-text-secondary mb-12 max-w-2xl mx-auto">
              {t('home.cta.description')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="group relative px-10 py-5 bg-gradient-to-r from-accent to-purple-600 rounded-xl font-semibold text-white text-lg shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all duration-300 hover:-translate-y-1"
              >
                <span className="flex items-center gap-3">
                  {t('home.cta.primaryButton')}
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              <Link to="/about" className="btn btn-secondary btn-lg">
                {t('home.cta.secondaryButton')}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
